import { useState, type ReactNode } from 'react'
import { isSellerEntity, SELLER_ENTITY_LABELS_RU, SellerEntity } from '@crm/shared'
import { apiErrorText } from '../../../shared/api/errorText'
import { toast } from '../../../shared/ui/toastStore'
import { KP_ENTITIES, DEFAULT_KP_ENTITY } from '../utils/kpEntities'
import type { KpFormat } from '../utils/downloadKp'

interface Props {
  /** Заголовок-подсказка (например, дата КП) — опционально. */
  subtitle?: string
  /** Юрлицо продажи заявки — выбрано по умолчанию. Нет (старая заявка) — СПЕЦМАШЭЛЕКТРО. */
  defaultEntityId?: string | null
  onClose: () => void
  /** Выбор формата, юрлица и (опц.) получателя. Может быть async (генерация/снапшот). */
  onPick: (format: KpFormat, entityId: string, recipient: string) => void | Promise<void>
}

// Модалка выбора параметров скачивания КП: от какого юрлица и в каком формате.
// Стиль выровнен с остальными модалками проекта (ReassignOwnerModal и др.):
// карточка max-w-md / rounded-xl, brand-акценты, адаптив через grid + p-4.
export function KpDownloadModal({ subtitle, defaultEntityId, onClose, onPick }: Props) {
  const requestEntity = isSellerEntity(defaultEntityId) ? defaultEntityId : null
  const [entityId, setEntityId] = useState<string>(requestEntity ?? DEFAULT_KP_ENTITY.id)
  const [recipient, setRecipient] = useState('')
  const [busy, setBusy] = useState<KpFormat | null>(null)

  // В списке — все юрлица продажи. Юрлицо без шаблона КП в KP_ENTITIES
  // скачать нельзя — окно скажет об этом (сейчас шаблоны есть у всех трёх).
  const kpEntity = KP_ENTITIES.find((e) => e.id === entityId)
  const excelDisabled = !kpEntity
  // Для юрлиц без готового PDF-шаблона (напр. ДИСТИНКТ) оставляем только Excel.
  const pdfDisabled = !kpEntity?.pdfEnabled

  const pick = async (format: KpFormat) => {
    if (busy) return
    if (format === 'pdf' && pdfDisabled) return
    if (format === 'excel' && excelDisabled) return
    setBusy(format)
    try {
      await onPick(format, entityId, recipient.trim())
      onClose()
    } catch (err) {
      // Раньше catch не было: ошибка генерации файла уходила в необработанный
      // reject, «Готовлю…» гасло — и казалось, что ничего не произошло (№308).
      // Модалка остаётся открытой, чтобы можно было повторить.
      // Английскую причину из браузера не прячем целиком: дописываем её в
      // скобках, чтобы по скриншоту было видно, что сломалось (14.10).
      const tech =
        err instanceof Error && err.message && !/[А-Яа-яЁё]/.test(err.message)
          ? ` (${err.message})`
          : ''
      toast.error(apiErrorText(err, `Не удалось сформировать файл КП — попробуйте ещё раз${tech}`))
    } finally {
      setBusy(null)
    }
  }

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4"
      onClick={onClose}
    >
      <div
        className="w-full max-w-md space-y-5 rounded-xl bg-white p-6 shadow-xl"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Шапка */}
        <div className="flex items-start justify-between">
          <div>
            <h2 className="text-lg font-semibold text-gray-900">Скачать КП</h2>
            {subtitle && <p className="mt-0.5 text-sm text-gray-500">{subtitle}</p>}
          </div>
          <button
            onClick={onClose}
            className="-mr-1.5 -mt-1.5 rounded-lg p-1.5 text-gray-400 transition-colors hover:bg-gray-100 hover:text-gray-600"
            title="Закрыть"
            aria-label="Закрыть"
          >
            <svg viewBox="0 0 24 24" fill="none" className="h-4 w-4">
              <path
                d="M6 6l12 12M18 6L6 18"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
              />
            </svg>
          </button>
        </div>

        {/* Юр. лицо */}
        <div>
          <label className="mb-1 block text-xs text-gray-500">Юр. лицо (от кого)</label>
          <select
            value={entityId}
            onChange={(e) => setEntityId(e.target.value)}
            disabled={!!busy}
            className="w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-brand-500 disabled:opacity-50"
          >
            {Object.values(SellerEntity).map((id) => (
              <option key={id} value={id}>
                {SELLER_ENTITY_LABELS_RU[id]}
              </option>
            ))}
          </select>
          {requestEntity && entityId !== requestEntity && (
            <p className="mt-1 text-xs text-amber-600">
              В заявке указано другое юрлицо — {SELLER_ENTITY_LABELS_RU[requestEntity]}.
            </p>
          )}
        </div>

        {/* Кому адресовано (необязательно) */}
        <div>
          <label className="mb-1 block text-xs text-gray-500">
            Кому адресовать (необязательно)
          </label>
          <input
            type="text"
            value={recipient}
            onChange={(e) => setRecipient(e.target.value)}
            disabled={!!busy}
            placeholder="Напр. Начальнику отдела снабжения"
            maxLength={120}
            className="w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-brand-500 disabled:opacity-50"
          />
          <p className="mt-1 text-xs text-gray-400">
            {kpEntity?.layout === 'mosel'
              ? 'Появится в PDF строкой под названием заказчика. Оставьте пустым — будет без получателя.'
              : 'Появится строкой «Кому: …» в PDF. Оставьте пустым — будет без получателя.'}
          </p>
        </div>

        {/* Формат файла */}
        <div>
          <label className="mb-1.5 block text-xs text-gray-500">Формат файла</label>
          <div className="grid grid-cols-2 gap-3">
            <FormatCard
              label="PDF"
              busy={busy === 'pdf'}
              disabled={!!busy || pdfDisabled}
              onClick={() => void pick('pdf')}
              iconClass="bg-red-50 text-red-600"
              icon={
                <>
                  <path
                    d="M14 3H7a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2V8l-5-5z"
                    stroke="currentColor"
                    strokeWidth="1.8"
                    strokeLinejoin="round"
                  />
                  <path
                    d="M14 3v5h5"
                    stroke="currentColor"
                    strokeWidth="1.8"
                    strokeLinejoin="round"
                  />
                </>
              }
            />
            <FormatCard
              label="Excel"
              busy={busy === 'excel'}
              disabled={!!busy || excelDisabled}
              onClick={() => void pick('excel')}
              iconClass="bg-emerald-50 text-emerald-600"
              icon={
                <>
                  <rect
                    x="3"
                    y="4"
                    width="18"
                    height="16"
                    rx="2"
                    stroke="currentColor"
                    strokeWidth="1.8"
                  />
                  <path d="M3 9h18M9 4v16" stroke="currentColor" strokeWidth="1.8" />
                </>
              }
            />
          </div>
          {excelDisabled ? (
            <p className="mt-2 text-xs text-amber-600">
              Шаблона КП для этого юр. лица пока нет — выберите другое юр. лицо.
            </p>
          ) : (
            pdfDisabled && (
              <p className="mt-2 text-xs text-amber-600">
                PDF для этого юр. лица пока в разработке — доступен только Excel.
              </p>
            )
          )}
        </div>
      </div>
    </div>
  )
}

// Карточка выбора формата: нейтральная по умолчанию, на hover подсвечивается
// brand-синим (как остальные интерактивные элементы), иконка — в цветном бейдже
// для узнаваемости типа файла.
function FormatCard({
  label,
  busy,
  disabled,
  onClick,
  icon,
  iconClass,
}: {
  label: string
  busy: boolean
  disabled: boolean
  onClick: () => void
  icon: ReactNode
  iconClass: string
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      className="group flex flex-col items-center gap-2 rounded-xl border border-gray-200 bg-white px-4 py-4 transition-colors hover:border-brand-400 hover:bg-brand-50 disabled:opacity-60 disabled:hover:border-gray-200 disabled:hover:bg-white"
    >
      <span
        className={`flex h-10 w-10 items-center justify-center rounded-lg ${iconClass}`}
        aria-hidden
      >
        <svg viewBox="0 0 24 24" fill="none" className="h-5 w-5">
          {icon}
        </svg>
      </span>
      <span className="text-sm font-medium text-gray-800">{busy ? 'Готовлю…' : label}</span>
    </button>
  )
}
